import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { CardButton } from '../../Compounds/Buttons/CardButton';
import {
  GridPreferences,
  getDefaultPreferences,
  getPreferencesByKey,
  getUserPreferencesSubject
} from '../../../store/User';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import { DefaultLineItemGridColumnDefs } from './LineItemGridColumnDefs';
import {
  getLineItemLoadingSubject,
  getLineItemProductLabel,
  getLineItemSubject,
  LineItem,
  useLineItemsStore
} from '../../../store/LineItem';
import { getProductSubject, useProductsStore } from '../../../store/Product';
import { Product } from '../../../types';

interface LineItemGridRow {
  id: string;
  product: string;
  description: string;
  mrc: number;
  nrc: number;
}

export const LineItemsPage = () => {
  const page = 'Line Items';
  const nameGrid = 'line_item_grid';
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [recordsLoading, setRecordsLoading] = useState<boolean>(true);
  const [orderColumns, setOrderColumns] = useState<GridSingleSelectColDef[]>();
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences().content[nameGrid] as GridPreferences
  );
  const [search] = useSearchParams();
  const recordId = search.get('recordId') || '';
  const navigate = useNavigate();
  const { fetchLineItems, setLineItems: resetLineItems } = useLineItemsStore();
  const { fetchProducts } = useProductsStore();

  const handleBack = () => {
    // A bookmarked/reloaded /line-items has no in-app history to return to; fall back to Inventory.
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/inventory');
  };

  const records: LineItemGridRow[] = useMemo(
    () =>
      lineItems.map((item) => ({
        id: item.id,
        product: getLineItemProductLabel(item.product_id),
        description: item.description,
        mrc: item.mrc || 0,
        nrc: item.nrc || 0
      })),
    // `products` re-maps the label once products load; getLineItemProductLabel reads them imperatively.
    [lineItems, products]
  );

  useEffect(() => {
    resetLineItems([]);
    if (recordId) fetchLineItems(recordId);
    fetchProducts();
  }, [recordId]);

  useEffect(() => {
    const lineItemSub = getLineItemSubject().subscribe((items) => setLineItems(items));
    const productSub = getProductSubject().subscribe((items) => setProducts(items));
    const loadingSub = getLineItemLoadingSubject().subscribe((loading) => setRecordsLoading(loading));
    const settingsSub = getUserPreferencesSubject().subscribe(() => {
      const gridPrefs = getPreferencesByKey(nameGrid) as GridPreferences;
      if (gridPrefs) setGridSettings(gridPrefs);
    });

    return () => {
      lineItemSub.unsubscribe();
      productSub.unsubscribe();
      loadingSub.unsubscribe();
      settingsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setOrderColumns(getGridColumns(records, DefaultLineItemGridColumnDefs, nameGrid));
  }, [records]);

  return (
    <PageLayout
      pageTitle={page}
      pageActions={
        <CardButton
          text="Back"
          onClick={handleBack}
          className="h-8 px-2 pl-4 border border-grey-500 rounded-lg shadow"
        />
      }>
      <PageCard
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title={`${page} (${records.length} Item${records.length === 1 ? '' : 's'})`}>
        {orderColumns ? (
          <div className="max-w-full">
            <UpstackDataGrid
              dataCy="line-items-data-grid"
              title="Line Items"
              rows={records}
              columns={orderColumns}
              loadingData={recordsLoading}
              page={nameGrid}
              gridSettings={gridSettings}
              showSearch
            />
          </div>
        ) : (
          <></>
        )}
      </PageCard>
      <div className="h-2"></div>
    </PageLayout>
  );
};
